import { useCallback, useEffect, useRef, useState } from 'react';

import lang from '../../../lib/language';

import InfoIcon from '../../../assets/img/icon-info-outline.svg';
import Button from '../../Button/Button';
import { ButtonTypesEnum } from '../../Button/Button.types';
import ClickableImage from '../../ClickableImage/ClickableImage';
import useIsMobile from '../../hooks/useIsMobile';
import MobileDrawer from '../../MobileDrawer/MobileDrawer';

import styles from './HighGrowthCompaniesInfo.module.scss';

const TOOLTIP_SCREEN_PADDING = 12;

const HighGrowthCompaniesInfo = () => {
  const { isMobile } = useIsMobile();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isTooltipCentered, setIsTooltipCentered] = useState<boolean>(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleOpenDrawer = () => setIsDrawerOpen(true);
  const handleCloseDrawer = () => setIsDrawerOpen(false);
  const tooltipClassName = `${styles.tooltip} ${isTooltipCentered ? styles.tooltipCentered : ''}`.trim();

  const updateTooltipPosition = useCallback(() => {
    if (isMobile || !triggerRef.current || !tooltipRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const iconCenterX = triggerRect.left + (triggerRect.width / 2);
    const tooltipHalfWidth = tooltipRect.width / 2;

    const canCenter = (iconCenterX - tooltipHalfWidth) >= TOOLTIP_SCREEN_PADDING
      && (iconCenterX + tooltipHalfWidth) <= (window.innerWidth - TOOLTIP_SCREEN_PADDING);

    setIsTooltipCentered(canCenter);
  }, [isMobile]);

  useEffect(() => {
    updateTooltipPosition();

    if (isMobile) {
      return undefined;
    }

    window.addEventListener('resize', updateTooltipPosition);
    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [isMobile, updateTooltipPosition]);

  const renderMobileContent = () => (
    <>
      <div className={styles.description}>{lang.commonGrowthIndicatorDescription()}</div>
      <Button
        biggerFontSize
        fullWidth
        onClick={handleCloseDrawer}
        variant={ButtonTypesEnum.Primary}
        className={styles.button}
      >
        {lang.commonClose()}
      </Button>
    </>
  );

  return (
    <>
      <span className={styles.triggerContainer} ref={triggerRef}>
        <Button
          variant={ButtonTypesEnum.Link}
          className={styles.triggerButton}
          onClick={isMobile ? handleOpenDrawer : undefined}
          ariaLabel={lang.commonDiscoverHighGrowthCompaniesLegend()}
        >
          <ClickableImage src={InfoIcon} variant="contained" className={styles.popoverTrigger} />
        </Button>
        {!isMobile && (
          <div className={tooltipClassName} role="tooltip" ref={tooltipRef}>
            {lang.commonGrowthIndicatorDescription()}
          </div>
        )}
      </span>
      {isMobile && (
        <MobileDrawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          title={lang.commonDiscoverHighGrowthCompaniesLegend()}
        >
          {renderMobileContent()}
        </MobileDrawer>
      )}
    </>
  );
};

export default HighGrowthCompaniesInfo;
